/**
 * EcoPtzGuide
 * ─────────────────────────────────────────────────────────────────
 * Guide éditorial complet pour /particuliers/aides/eco-ptz.
 *
 * Structure :
 *  1. Hero (eyebrow + titre + lead + sidecard + CTA)
 *  2. Bandeau "En résumé"
 *  3. Mécanisme (comment ça marche)
 *  4. Plafonds par type de travaux (tableau)
 *  5. Travaux éligibles (3 catégories)
 *  6. Conditions (4 cartes)
 *  7. Démarches (5 étapes)
 *  8. Cumul avec les autres aides
 *  9. FAQ
 * 10. CTA final
 *
 * Contenu : `client/src/config/eco-ptz-content.ts` (source unique).
 */

import { ArrowRight, CheckCircle2, Info } from 'lucide-react';

import { Container } from '../layout/Container';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { ArticleLd } from '../seo/jsonld/Article';
import { BreadcrumbListLd } from '../seo/jsonld/BreadcrumbList';
import { FAQPageLd } from '../seo/jsonld/FAQPage';
import { HowToLd } from '../seo/jsonld/HowTo';
import { getIcon } from '../../lib/lucide-icons';
import { cn } from '../../lib/cn';
import {
  CTA_HREF,
  SectionHeader,
  SITE_URL,
} from './_shared';
import {
  PARTICULIER_URLS,
  ParticulierCTAFinal,
  ParticulierLiveCard,
  ParticulierPageHero,
} from '../audience/particulier';
import {
  ECO_PTZ_CONDITIONS,
  ECO_PTZ_CUMULS,
  ECO_PTZ_DEMARCHES,
  ECO_PTZ_FAQ,
  ECO_PTZ_HERO,
  ECO_PTZ_MECANISME,
  ECO_PTZ_PLAFONDS,
  ECO_PTZ_SUMMARY,
  ECO_PTZ_TRAVAUX,
} from '../../config/eco-ptz-content';

const PAGE_URL = `${SITE_URL}/particuliers/aides/eco-ptz`;

const CUMUL_TONE_STYLES: Record<
  'brand' | 'accent' | 'sage' | 'lavender' | 'ivory',
  string
> = {
  brand: 'bg-brand-50 text-brand-700 ring-brand-200/60',
  accent: 'bg-accent-50 text-accent-800 ring-accent-200/60',
  sage: 'bg-stone-100 text-stone-700 ring-stone-200/60',
  lavender: 'bg-brand-50 text-brand-700 ring-brand-200/60',
  ivory: 'bg-ivory-100 text-stone-700 ring-stone-200/60',
};

// ════════════════════════════════════════════════════════════════
// Composant principal
// ════════════════════════════════════════════════════════════════

export function EcoPtzGuide() {
  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: `${SITE_URL}/` },
          { name: 'Particuliers', url: `${SITE_URL}/particuliers` },
          { name: 'Aides', url: `${SITE_URL}/particuliers/aides` },
          { name: 'Éco-PTZ', url: PAGE_URL },
        ]}
      />
      <ArticleLd
        headline="Éco-PTZ 2026 — Prêt à taux zéro jusqu'à 50 000 €"
        url={PAGE_URL}
        datePublished="2026-01-01"
        dateModified={new Date().toISOString().slice(0, 10)}
      />
      <FAQPageLd
        items={ECO_PTZ_FAQ.map((item) => ({
          question: item.question,
          answer: item.answer,
        }))}
      />
      <HowToLd
        name="Comment obtenir l'éco-PTZ en 2026"
        description="Les 5 étapes pour bénéficier de l'éco-prêt à taux zéro auprès de votre banque."
        steps={ECO_PTZ_DEMARCHES.map((d) => ({ name: d.title, text: d.description }))}
      />

      <HeroSection />
      <SummarySection />
      <MecanismeSection />
      <PlafondsSection />
      <TravauxSection />
      <ConditionsSection />
      <DemarchesSection />
      <CumulSection />
      <FaqSection />
      <FinalCtaSection />
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// 1. HERO
// ════════════════════════════════════════════════════════════════

function HeroSection() {
  const caracteristiques = [
    'Aucun intérêt',
    'Aucun frais de dossier',
    'Sans condition de ressources',
    'Durée jusqu’à 20 ans',
  ];
  return (
    <ParticulierPageHero
      breadcrumb={[
        { name: 'Aides', href: '/particuliers/aides' },
        { name: 'Éco-PTZ' },
      ]}
      eyebrow={ECO_PTZ_HERO.eyebrow}
      headline={ECO_PTZ_HERO.titlePrimary}
      headlineAccent={ECO_PTZ_HERO.titleAccent}
      subheadline={ECO_PTZ_HERO.lead}
      primaryCta={{ label: 'Estimer mon plan de financement', href: CTA_HREF }}
      secondaryCta={{ label: 'Voir les plafonds par type de travaux', href: '#plafonds' }}
      microPromise={ECO_PTZ_HERO.microPromise}
      sideSlot={
        <ParticulierLiveCard eyebrow="Plafond maximal">
          <p className="bg-gradient-to-br from-accent-700 via-accent-600 to-brand-600 bg-clip-text text-4xl font-black leading-none tracking-tight text-transparent">
            {ECO_PTZ_HERO.amountHeadline}
          </p>
          <p className="mt-3 text-[13px] leading-relaxed text-stone-500">
            {ECO_PTZ_HERO.amountSubline}
          </p>
          <div className="mt-5 border-t border-stone-100 pt-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400">
              Caractéristiques
            </p>
            <ul className="mt-3 space-y-1.5 text-[13px] text-stone-700">
              {caracteristiques.map((label) => (
                <li key={label} className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3 w-3 shrink-0 text-brand-500" strokeWidth={2.4} />
                  {label}
                </li>
              ))}
            </ul>
          </div>
        </ParticulierLiveCard>
      }
    />
  );
}

// ════════════════════════════════════════════════════════════════
// 2. EN RÉSUMÉ
// ════════════════════════════════════════════════════════════════

function SummarySection() {
  return (
    <section className="border-y border-stone-200/70 bg-stone-50/60 py-10 md:py-14">
      <Container>
        <div className="rounded-2xl border border-stone-200 bg-paper p-6 shadow-subtle md:p-8">
          <div className="mb-5 flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-brand-100 text-brand-700">
              <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
            </span>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-500">
              En résumé
            </p>
          </div>
          <ul className="grid gap-3 md:grid-cols-2">
            {ECO_PTZ_SUMMARY.map((point) => (
              <li
                key={point.text}
                className="flex items-start gap-2.5 rounded-xl border border-stone-100 bg-stone-50/50 p-3.5"
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" strokeWidth={2.4} />
                <span className="text-[13.5px] leading-relaxed text-stone-700">{point.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 3. MÉCANISME
// ════════════════════════════════════════════════════════════════

function MecanismeSection() {
  return (
    <section className="py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow={ECO_PTZ_MECANISME.eyebrow}
          title={ECO_PTZ_MECANISME.title}
          subtitle={ECO_PTZ_MECANISME.description}
        />

        <div className="grid gap-4 sm:grid-cols-3">
          <FlowStep
            number={1}
            iconName="landmark"
            title="Vous empruntez à la banque"
            text="La banque vous prête le capital nécessaire au financement de vos travaux d’amélioration énergétique."
          />
          <FlowStep
            number={2}
            iconName="trending-down"
            title="Aucun intérêt facturé"
            text="L’État rembourse les intérêts à la banque sous forme de crédit d’impôt. Côté ménage, vous ne payez que le capital."
          />
          <FlowStep
            number={3}
            iconName="wallet"
            title="Vous remboursez le capital"
            text="Mensualités fixes étalées sur 15 ans (20 ans pour rénovation globale). Aucun frais caché, aucune mauvaise surprise."
          />
        </div>
      </Container>
    </section>
  );
}

function FlowStep({
  number,
  iconName,
  title,
  text,
}: {
  number: number;
  iconName: string;
  title: string;
  text: string;
}) {
  const Icon = getIcon(iconName);
  return (
    <article className="rounded-2xl border border-stone-200 bg-paper p-5 shadow-subtle">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-brand-600 to-accent-600 text-base font-bold text-white shadow-glow">
          {number}
        </span>
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60">
          <Icon className="h-4 w-4" strokeWidth={1.7} />
        </span>
      </div>
      <h3 className="mt-4 font-display text-base font-bold text-ink">{title}</h3>
      <p className="mt-1.5 text-[13px] leading-relaxed text-stone-600">{text}</p>
    </article>
  );
}

// ════════════════════════════════════════════════════════════════
// 4. PLAFONDS PAR TYPE DE TRAVAUX
// ════════════════════════════════════════════════════════════════

function PlafondsSection() {
  return (
    <section id="plafonds" className="border-t border-stone-100 bg-stone-50/40 py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Plafonds par type de travaux"
          title="Combien pouvez-vous emprunter ?"
          subtitle="Le plafond de l’éco-PTZ varie selon le type de travaux et le nombre d’actions. Le maximum (50 000 €) n’est atteint que pour les rénovations globales avec gain énergétique d’au moins 35 %, validées par un audit."
        />

        <div className="overflow-hidden rounded-2xl border border-stone-200 bg-paper shadow-subtle">
          <div className="overflow-x-auto">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="border-b border-stone-100 bg-stone-50/60 text-left text-[10.5px] font-bold uppercase tracking-[0.1em] text-stone-500">
                  <th className="px-5 py-3.5 font-semibold">Type d’action</th>
                  <th className="px-5 py-3.5 font-semibold">Plafond du prêt</th>
                  <th className="px-5 py-3.5 font-semibold">Durée max</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {ECO_PTZ_PLAFONDS.map((row) => (
                  <tr key={row.action} className="transition-colors hover:bg-stone-50/40">
                    <td className="px-5 py-3 align-top text-stone-800">
                      <div className="font-medium">{row.action}</div>
                      {row.note ? (
                        <div className="mt-0.5 text-[11.5px] italic text-stone-500">{row.note}</div>
                      ) : null}
                    </td>
                    <td className="px-5 py-3 align-top font-semibold text-brand-700">{row.plafond}</td>
                    <td className="px-5 py-3 align-top text-stone-700">{row.duree}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="mt-5 flex items-start gap-2.5 rounded-xl bg-stone-100/60 p-4 text-[12.5px] leading-relaxed text-stone-600 ring-1 ring-inset ring-stone-200">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-500" strokeWidth={1.8} />
          Source : article 244 quater U du Code général des impôts. Plafonds en vigueur en
          2026, susceptibles d’évolution réglementaire.
        </p>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 5. TRAVAUX ÉLIGIBLES
// ════════════════════════════════════════════════════════════════

function TravauxSection() {
  return (
    <section className="py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Travaux éligibles"
          title="Les trois catégories couvertes par l’éco-PTZ"
          subtitle="Les travaux doivent appartenir à une catégorie reconnue (chauffage performant, isolation, ENR, ventilation double flux). La rénovation globale combine plusieurs catégories pour atteindre le gain énergétique requis."
        />

        <div className="grid gap-5 md:grid-cols-3">
          {ECO_PTZ_TRAVAUX.map((cat) => {
            const Icon = getIcon(cat.iconName);
            return (
              <article
                key={cat.title}
                className="flex flex-col gap-4 rounded-2xl border border-stone-200 bg-paper p-6 shadow-subtle"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60">
                  <Icon className="h-5 w-5" strokeWidth={1.7} />
                </span>
                <h3 className="font-display text-lg font-bold text-ink">{cat.title}</h3>
                <ul className="space-y-2">
                  {cat.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2 text-[13px] leading-relaxed text-stone-700"
                    >
                      <CheckCircle2
                        className="mt-1 h-3.5 w-3.5 shrink-0 text-brand-500"
                        strokeWidth={2.2}
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 6. CONDITIONS
// ════════════════════════════════════════════════════════════════

function ConditionsSection() {
  return (
    <section className="border-t border-stone-100 bg-stone-50/40 py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Conditions"
          title="Les 4 conditions pour bénéficier de l’éco-PTZ"
          subtitle="Aucune condition de ressources, mais 4 critères techniques et administratifs à respecter."
        />

        <div className="grid gap-4 sm:grid-cols-2">
          {ECO_PTZ_CONDITIONS.map((c) => {
            const Icon = getIcon(c.iconName);
            return (
              <article
                key={c.title}
                className="rounded-2xl border border-stone-200 bg-paper p-5 shadow-subtle"
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60">
                  <Icon className="h-5 w-5" strokeWidth={1.7} />
                </span>
                <p className="mt-3 font-display text-base font-bold text-ink">{c.title}</p>
                <p className="mt-1.5 text-[13px] leading-relaxed text-stone-600">{c.description}</p>
              </article>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 7. DÉMARCHES
// ════════════════════════════════════════════════════════════════

function DemarchesSection() {
  return (
    <section className="py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Démarches"
          title="5 étapes pour obtenir votre éco-PTZ"
          subtitle="L’éco-PTZ se demande auprès de votre banque, après avoir fait établir un devis par un artisan RGE. Le formulaire officiel est fourni par votre banque ou téléchargeable sur service-public.fr."
        />

        <ol className="grid gap-4 md:gap-3">
          {ECO_PTZ_DEMARCHES.map((step, idx) => {
            const Icon = getIcon(step.iconName);
            const isLast = idx === ECO_PTZ_DEMARCHES.length - 1;
            return (
              <li
                key={step.step}
                className="relative flex items-start gap-5 rounded-2xl border border-stone-200 bg-paper p-5 shadow-subtle md:p-6"
              >
                <div className="flex shrink-0 flex-col items-center gap-2">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-600 to-accent-600 text-base font-bold text-white shadow-glow">
                    {step.step}
                  </span>
                  {!isLast ? (
                    <span aria-hidden className="hidden h-full w-px flex-1 bg-stone-200 md:block" />
                  ) : null}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60">
                      <Icon className="h-4 w-4" strokeWidth={1.7} />
                    </span>
                    <h3 className="font-display text-base font-bold text-ink md:text-lg">{step.title}</h3>
                  </div>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-stone-600">{step.description}</p>
                </div>
              </li>
            );
          })}
        </ol>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 8. CUMUL
// ════════════════════════════════════════════════════════════════

function CumulSection() {
  return (
    <section className="border-t border-stone-100 bg-stone-50/40 py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Cumul avec les autres aides"
          title="L’éco-PTZ s’ajoute à toutes les autres aides"
          subtitle="Cumul natif avec MaPrimeRénov’, prime énergie (CEE) et TVA 5,5 %. L’éco-PTZ finance souvent la part qui reste à votre charge après subventions."
        />

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {ECO_PTZ_CUMULS.map((c) => {
            const Icon = getIcon(c.iconName);
            const Tag = c.href ? 'a' : 'div';
            return (
              <Tag
                key={c.aide}
                {...(c.href ? { href: c.href } : {})}
                className={cn(
                  'group flex flex-col gap-3 rounded-2xl border border-stone-200 bg-paper p-5 shadow-subtle',
                  c.href && 'transition-all duration-200 hover:-translate-y-0.5 hover:shadow-medium',
                )}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={cn(
                      'inline-flex h-9 w-9 items-center justify-center rounded-lg ring-1 ring-inset',
                      CUMUL_TONE_STYLES[c.tone],
                    )}
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.7} />
                  </span>
                  {c.href ? (
                    <ArrowRight
                      className="h-3.5 w-3.5 text-stone-400 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-brand-600"
                      strokeWidth={2.4}
                    />
                  ) : null}
                </div>
                <p className="text-[14px] font-semibold text-ink">{c.aide}</p>
                <p className="text-[12.5px] leading-relaxed text-stone-600">{c.description}</p>
              </Tag>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 9. FAQ
// ════════════════════════════════════════════════════════════════

function FaqSection() {
  return (
    <section className="py-14 md:py-20">
      <Container size="narrow">
        <SectionHeader
          eyebrow="FAQ"
          title="Vos questions sur l’éco-PTZ"
          subtitle="Les questions essentielles posées par les propriétaires en 2026."
          align="center"
        />

        <div className="overflow-hidden rounded-2xl border border-stone-200 bg-paper shadow-subtle">
          <Accordion type="single" collapsible className="divide-y divide-stone-100">
            {ECO_PTZ_FAQ.map((item, idx) => (
              <AccordionItem
                key={item.question}
                value={`ecoptz-faq-${idx}`}
                className="border-0 px-5"
              >
                <AccordionTrigger className="text-[15px]">{item.question}</AccordionTrigger>
                <AccordionContent className="text-[14px] leading-relaxed text-stone-600">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 10. CTA FINAL
// ════════════════════════════════════════════════════════════════

function FinalCtaSection() {
  return (
    <ParticulierCTAFinal
      badge="Plan de financement personnalisé"
      title="Construisons votre plan de financement complet"
      description={
        <>
          Notre calculateur croise MaPrimeRénov&rsquo;, prime énergie (CEE), TVA 5,5 % et
          éco-PTZ pour vous présenter le plan optimal : subventions + reste à charge
          financé sans intérêts.
        </>
      }
      primaryCta={{ label: 'Lancer la simulation', href: CTA_HREF }}
      secondaryCta={{ label: 'Parler à un conseiller', href: PARTICULIER_URLS.contact }}
    />
  );
}
